import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Input } from "../../../../components/ui/input";
import {
  useOptroFilterOptions,
  useOptroImportControls,
} from "../../../../hooks/useOptroImportControls";
import { SelectControlsTable } from "./components/SelectControlsTable";
import { FilterCombobox } from "./components/FilterCombobox";
import { OptroConnectDialog } from "./components/OptroConnectDialog";
import { useOptroImportWizard } from "./optroImportWizardContext";
import { useOptroConnection } from "../../../../hooks/useOptroConnection";

export const OptroImportSelectPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [processId, setProcessId] = useState<number | null>(null);
  const [ownerUserId, setOwnerUserId] = useState<number | null>(null);
  // Defaults to only showing controls not yet imported into Midship.
  const [excludeLinked, setExcludeLinked] = useState(true);

  const { options: filterOptions } = useOptroFilterOptions();

  const {
    controls,
    loading,
    loadingMore,
    hasLoadedOnce,
    error,
    retry,
    loadMore,
    hasMore,
    fetchAllRemaining,
  } = useOptroImportControls(search, { processId, ownerUserId, excludeLinked });
  const { selected, toggleControl, isSelected, selectMany, clearSelection } =
    useOptroImportWizard();

  // Header select-all: with anything selected, the checkbox clears the whole
  // selection; otherwise it selects EVERY control matching the current
  // search/filters, fetching the not-yet-loaded pages first so there's no
  // "loaded rows only" ambiguity.
  const [selectingAll, setSelectingAll] = useState(false);
  const selectableLoaded = controls.filter((c) => !c.linked && !c.importing);
  const allLoadedSelected =
    selectableLoaded.length > 0 &&
    selectableLoaded.every((c) => isSelected(c.externalRef));
  const headerChecked: boolean | "indeterminate" =
    selected.length === 0
      ? false
      : !hasMore && allLoadedSelected
        ? true
        : "indeterminate";

  const onToggleAll = async () => {
    if (selected.length > 0) {
      clearSelection();
      return;
    }
    setSelectingAll(true);
    const result = await fetchAllRemaining();
    setSelectingAll(false);
    if (result.status === "ok") {
      selectMany(result.controls.filter((c) => !c.linked && !c.importing));
    } else if (result.status === "error") {
      toast.error("Couldn't load all matching controls. Try again.");
    }
    // "superseded" (filters changed mid-fetch): do nothing — the new result
    // set is loading and selecting stale rows would be wrong.
  };

  const { connected: optroConnected, loading: connectionLoading } =
    useOptroConnection();
  const [gateDialogOpen, setGateDialogOpen] = useState(true);

  // Full-page interstitial only before the very first load has ever
  // completed; search/filter refetches keep the table visible (dimmed, with
  // a spinner overlay) instead.
  const initialLoad = loading && !hasLoadedOnce;
  const refetching = loading && hasLoadedOnce;

  // Changing a filter resets the list; scroll the table back to the top so
  // the (now-shorter or reordered) result set starts in view.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [processId, ownerUserId, excludeLinked]);

  // Infinite scroll observer setup
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading || loadingMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore, loadMore],
  );

  if (connectionLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!optroConnected) {
    return (
      <OptroConnectDialog
        open={gateDialogOpen}
        onOpenChange={(open) => {
          setGateDialogOpen(open);
          if (!open) {
            navigate("/audit/controls/v2");
          }
        }}
        onConnected={() => {
          setGateDialogOpen(false);
        }}
      />
    );
  }

  return (
    <div className="h-screen flex flex-col w-full p-6 max-w-[1200px] mx-auto">
      <h1 className="text-2xl font-semibold">Select Controls to Import</h1>
      <p className="text-sm text-stone-500 mt-1 mb-4">
        Choose the controls to bring into Midship.
      </p>

      {initialLoad ? (
        <div className="border rounded-lg py-24 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-stone-400" />
          <p className="mt-4 font-medium">Loading controls from Optro...</p>
          <p className="mt-1 text-sm text-stone-500 max-w-md mx-auto">
            Pulling your control library from the connected Optro instance. This
            can take a moment for large libraries.
          </p>
        </div>
      ) : error ? (
        <div className="border rounded-lg py-24 text-center">
          <p className="font-medium">Could not load controls from Optro.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              // The error branch hides the search box, so clear any search
              // term that may itself be the failure trigger before retrying.
              setSearch("");
              retry();
            }}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3 shrink-0">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search controls..."
                className="pl-9"
              />
            </div>

            <FilterCombobox
              options={filterOptions?.processes ?? []}
              value={processId}
              onChange={setProcessId}
              allLabel="All processes"
              searchPlaceholder="Search processes..."
              emptyMessage="No processes found."
              disabled={!filterOptions}
            />

            <FilterCombobox
              options={filterOptions?.owners ?? []}
              value={ownerUserId}
              onChange={setOwnerUserId}
              allLabel="All owners"
              searchPlaceholder="Search owners..."
              emptyMessage="No owners found."
              disabled={!filterOptions}
            />

            <div className="flex items-center gap-2 shrink-0">
              <Checkbox
                id="exclude-linked"
                checked={excludeLinked}
                onCheckedChange={(checked) =>
                  setExcludeLinked(checked === true)
                }
              />
              <label
                htmlFor="exclude-linked"
                className="text-sm text-stone-600 whitespace-nowrap cursor-pointer"
              >
                Not imported yet
              </label>
            </div>
          </div>
          {/* The table is the page's only scroll region; the action bar below
              stays pinned on screen while lazy loading appends rows here. A
              search/filter refetch dims the current rows under a centered
              spinner and blocks interaction until fresh results land. */}
          <div className="relative flex-1 min-h-0">
            <div
              ref={scrollContainerRef}
              className={
                refetching
                  ? "h-full border rounded-lg overflow-y-auto opacity-50 pointer-events-none"
                  : "h-full border rounded-lg overflow-y-auto"
              }
              aria-busy={refetching}
            >
              <SelectControlsTable
                controls={controls}
                isSelected={isSelected}
                onToggle={toggleControl}
                loadingMore={loadingMore}
                lastRowRef={lastRowRef}
                headerChecked={headerChecked}
                onToggleAll={() => void onToggleAll()}
                selectingAll={selectingAll}
              />
            </div>
            {refetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-stone-500" />
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-center justify-between pt-4">
            <span className="text-sm text-stone-600">
              {selected.length} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/audit/controls/v2")}
              >
                Back
              </Button>
              <Button
                disabled={selected.length === 0}
                onClick={() =>
                  navigate("/audit/controls/v2/import/optro/review")
                }
              >
                Continue
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
